import { MeterType, readClock, TOD_OFFSET_MINUTES } from "@next-platform/packages-ts-types";
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;
const parseClockStart = (start) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(start);
    if (!match) {
        return null;
    }
    // The story-local start is always day 1; date-mode clocks carry no time part.
    return { day: 1, minuteOfDay: match[4] !== undefined ? Number(match[4]) * MINUTES_PER_HOUR + Number(match[5]) : -1 };
};
/** Index of the level whose inclusive `to` bound contains `value` (last `to: null` = open). -1 = no levels. */
const levelIndexForValue = (levels, value) => {
    if (!levels?.length) {
        return -1;
    }
    const index = levels.findIndex((level) => value <= (level.to ?? Infinity));
    return index === -1 ? levels.length - 1 : index;
};
/**
 * Tile value for "What you'll see in chat": bounded meters and counters show their start value
 * (counters formatted to `decimals` and suffixed with `unit`), dice show the die, and the clock
 * shows its engine readout at the start value ("Day 1 · HH:MM" / "Wed 1 Jan · HH:MM"; no time part
 * in date mode).
 */
export default function formatMeterStartValue(meter) {
    if (meter.type === MeterType.DICE) {
        return `d${meter.sides}`;
    }
    if (meter.type === MeterType.CLOCK) {
        return readClock(meter, 0).short;
    }
    const value = meter.default;
    const formatted = meter.type === MeterType.NUMBER ? value.toFixed(meter.decimals ?? 0) : String(value);
    return meter.type === MeterType.NUMBER && meter.unit ? `${formatted} ${meter.unit}` : formatted;
}
const clockStartCaption = (meter) => {
    const parsed = parseClockStart(meter.start);
    if (!parsed) {
        return undefined;
    }
    if (parsed.minuteOfDay >= 0 && meter.mode !== "date") {
        const minutesSinceOffset = (parsed.minuteOfDay - TOD_OFFSET_MINUTES + MINUTES_PER_DAY) % MINUTES_PER_DAY;
        const index = levelIndexForValue(meter.tod_levels, minutesSinceOffset);
        return index === -1 ? undefined : meter.tod_levels[index]?.caption;
    }
    const index = levelIndexForValue(meter.day_levels, parsed.day);
    return index === -1 ? undefined : meter.day_levels[index]?.caption;
};
/** Caption of the level the start value lands in, if that level carries one. */
export function meterStartLevelCaption(meter) {
    if (meter.type === MeterType.DICE) {
        return undefined;
    }
    if (meter.type === MeterType.CLOCK) {
        return clockStartCaption(meter);
    }
    const index = levelIndexForValue(meter.levels, meter.default);
    return index === -1 ? undefined : meter.levels[index]?.caption;
}
